import {
  MAX_MINI_INTERVIEW_MINUTES,
  MAX_SHORT_INTERVIEW_MINUTES,
} from "../constants";
import type {
  CompletionCode,
  CompletionCodeUpdateInput,
  TargetGroupRestrictedId,
  TargetGroupWithRestricted,
} from "../types";
import {
  APPROVE_REWARD_MINI_CENTS_PER_HOUR,
  APPROVE_REWARD_REGULAR_CENTS_PER_HOUR,
  APPROVE_REWARD_SHORT_CENTS_PER_HOUR,
  FIXED_SCREEN_OUT_CODE_TYPE,
  FIXED_SCREEN_OUT_PAYMENT_ACTION_TYPE,
  MAX_REWARD_CENTS_PER_HOUR,
  MAX_FIXED_SCREENOUT_REWARD_CENTS,
  MIN_FIXED_SCREENOUT_REWARD_CENTS,
  MIN_REWARD_TOTAL_CENTS,
  PROLIFIC_FEE_SHARE_OF_TOTAL,
  SCREENER_OVERHEAD_MINUTES,
  SCREENOUT_REWARD_CENTS_PER_HOUR,
  VIDEO_STUDY_ADDITIONAL_REWARD_CENTS_PER_HOUR,
} from "./constants";
import type {
  ProlificParticipantId,
  ProlificStudyId,
  ProlificSubmissionId,
  StudyStatus,
} from "./types";
import { notNull, isNullish } from "@/utils/helpers";
import { SearchParamsSync } from "@/utils/types";

export const getStudyStatusToRestoreAfterAddingPlaces = ({
  originalStatus,
  keepCompletedStudyOpen,
}: {
  originalStatus: StudyStatus;
  keepCompletedStudyOpen: boolean;
}): "PAUSE" | "STOP" | null => {
  if (originalStatus === "PAUSED") return "PAUSE";
  if (originalStatus === "COMPLETED" && !keepCompletedStudyOpen) return "STOP";
  // Adding places reactivates a completed study. Leave it active so replacement
  // participants can fill responses rejected by quality review.
  return null;
};

/**
 * Prolific pauses a study by itself when the study already holds every
 * submission it asked for and one of them is returned or rejected. That pause
 * always follows AWAITING REVIEW. A PAUSED event that follows any other status
 * is a pause someone requested, and it must stay.
 */
export const shouldResumeProlificStudyAfterEarlyExit = ({
  groupStatus,
  statusEventsNewestFirst,
}: {
  groupStatus: StudyStatus;
  statusEventsNewestFirst: readonly string[];
}): boolean => {
  if (groupStatus === "AWAITING REVIEW") return true;
  if (groupStatus !== "PAUSED") return false;
  const [newestStatus, ...olderStatuses] = statusEventsNewestFirst;
  if (newestStatus !== "PAUSED") return false;
  const statusBeforePause = olderStatuses.find((status) => status !== "PAUSED");
  return statusBeforePause === "AWAITING REVIEW";
};

export const get_prolific_study_id = (
  searchParams: Record<string, string | string[]>
) => {
  if (!is_prolific_participant(searchParams)) {
    return null;
  }
  return searchParams["__study_id"] as ProlificStudyId;
};

export const get_prolific_participant_id = (
  searchParams: Record<string, string | string[]>
): ProlificParticipantId | null => {
  if (!is_prolific_participant(searchParams)) {
    return null;
  }
  return searchParams["__pid"] as ProlificParticipantId;
};

export const get_prolific_submission_id = (
  searchParams: Record<string, string | string[]>
) => {
  if (!is_prolific_participant(searchParams)) {
    return null;
  }
  return searchParams["__submission_id"] as ProlificSubmissionId;
};

// Prolific documents its ids as lowercase hex of length 24, so anything else in
// the url params was not issued by Prolific.
export const isProlificId = (value: string): boolean =>
  /^[a-f\d]{24}$/.test(value);

// For ids read back from our own storage as plain text.
export const isProlificSubmissionId = (
  value: string
): value is ProlificSubmissionId => isProlificId(value);

export const is_prolific_participant = (searchParams: SearchParamsSync) => {
  const pid = searchParams["__pid"];
  const study_id = searchParams["__study_id"];
  const submission_id = searchParams["__submission_id"];
  const missing_param = !(
    notNull(pid) &&
    notNull(study_id) &&
    notNull(submission_id)
  );
  if (missing_param) {
    return false;
  }
  const params_unique =
    !Array.isArray(pid) &&
    !Array.isArray(study_id) &&
    !Array.isArray(submission_id);
  if (!params_unique) {
    return false;
  }
  return (
    isProlificId(pid) && isProlificId(study_id) && isProlificId(submission_id)
  );
};

/**
 * returns the amount (in cents!) that should pay a participant for
 * completing a study with a given time estimate
 */
export const prolific_study_reward = (
  estimated_completion_time: number,
  config: { need_camera?: boolean } = {}
) => {
  let rate =
    estimated_completion_time <= MAX_MINI_INTERVIEW_MINUTES
      ? APPROVE_REWARD_MINI_CENTS_PER_HOUR
      : estimated_completion_time <= MAX_SHORT_INTERVIEW_MINUTES
        ? APPROVE_REWARD_SHORT_CENTS_PER_HOUR
        : APPROVE_REWARD_REGULAR_CENTS_PER_HOUR;
  if (config.need_camera) {
    rate += VIDEO_STUDY_ADDITIONAL_REWARD_CENTS_PER_HOUR;
  }
  rate = Math.min(rate, MAX_REWARD_CENTS_PER_HOUR);
  return Math.max(
    Math.ceil((rate * estimated_completion_time) / 60),
    MIN_REWARD_TOTAL_CENTS
  );
};

/** The fee Prolific charges on top of a reward, in the reward's cents. */
export const calculateProlificFeeCents = (rewardCents: number) =>
  Math.round(
    (rewardCents * PROLIFIC_FEE_SHARE_OF_TOTAL) /
      (1 - PROLIFIC_FEE_SHARE_OF_TOTAL)
  );

/**
 * returns the fixed screen-out reward (in cents!) for a screener with a given
 * time estimate. Prolific rejects screen-out rewards above the study reward.
 */
export const prolificScreenOutReward = ({
  estimatedScreenerMinutes,
  studyRewardCents,
}: {
  estimatedScreenerMinutes: number;
  studyRewardCents: number;
}) =>
  Math.min(
    Math.max(
      Math.ceil(
        (SCREENOUT_REWARD_CENTS_PER_HOUR *
          (estimatedScreenerMinutes + SCREENER_OVERHEAD_MINUTES)) /
          60
      ),
      MIN_FIXED_SCREENOUT_REWARD_CENTS
    ),
    MAX_FIXED_SCREENOUT_REWARD_CENTS,
    studyRewardCents
  );

/**
 * Builds a completion code update for adding screening slots to a fixed screenout code.
 * Returns undefined if there's no fixed screenout code, no current slots, or no new slots to add.
 */
export const buildCompletionCodeUpdate = (
  completionCodes: CompletionCode[],
  newScreeningSlots: number
): CompletionCodeUpdateInput | undefined => {
  const fixedScreenOutCode = completionCodes.find(
    (code) => code.code_type === FIXED_SCREEN_OUT_CODE_TYPE
  );
  const currentSlots = notNull(fixedScreenOutCode)
    ? fixedScreenOutCode.actions.find(
        (action) => action.action === FIXED_SCREEN_OUT_PAYMENT_ACTION_TYPE
      )?.slots
    : undefined;

  if (
    isNullish(fixedScreenOutCode) ||
    isNullish(currentSlots) ||
    newScreeningSlots <= 0
  ) {
    return undefined;
  }

  return {
    code: fixedScreenOutCode.code,
    actions: [
      {
        action: FIXED_SCREEN_OUT_PAYMENT_ACTION_TYPE,
        slots: currentSlots + newScreeningSlots,
      },
    ],
  };
};

/**
 * Picks the completion codes to persist for a target group after a
 * create_prolific_studies run. Only groups whose Prolific study was newly
 * created (reported in modifiedRestrictedIds) take the freshly generated
 * codes; reused groups keep their stored codes, which still match their live
 * study. Writing new codes for a reused group desyncs stored vs live codes
 * and strands in-flight participants on a code Prolific can't action.
 */
export const completionCodesForGroup = ({
  group,
  modifiedRestrictedIds,
  newCodes,
}: {
  group: TargetGroupWithRestricted;
  modifiedRestrictedIds: Set<TargetGroupRestrictedId>;
  newCodes: CompletionCode[];
}): CompletionCode[] =>
  modifiedRestrictedIds.has(group.restricted.id)
    ? newCodes
    : (group.restricted.data?.completion_codes ?? []);

// Only groups with open places become studies in the collection; saturated groups reuse their existing study.
export const countOpenProlificGroups = (
  groups: TargetGroupWithRestricted[]
): number =>
  groups.filter(
    (group) =>
      group.panel === "prolific" &&
      group.total_available_places -
        group.restricted.active_count -
        group.restricted.complete_count >
        0
  ).length;
